/**
 * Payoff-at-expiry diagram for an option structure.
 *
 * Drawn from the legs rather than from a server-supplied curve, so the picture
 * and the numbers beside it cannot drift apart. The shaded band is the market's
 * one-standard-deviation expected move: a structure whose profit zone sits
 * entirely outside it is betting against the price of the options themselves.
 */
import type { StructureLeg } from "@tt/shared";

export function PayoffChart({
  legs, spot, expectedMove, breakevens, width = 420, height = 150,
}: {
  legs: StructureLeg[];
  spot: number;
  expectedMove?: number | null;
  breakevens?: number[];
  width?: number;
  height?: number;
}) {
  if (!legs.length || !Number.isFinite(spot) || spot <= 0) return null;

  const strikes = legs.map((l) => l.strike);
  const span = Math.max(
    spot * 0.22,
    (Math.max(...strikes) - Math.min(...strikes)) * 1.6,
    (expectedMove ?? 0) * 2.2,
  );
  const lo = Math.max(0.01, spot - span);
  const hi = spot + span;

  const N = 240;
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < N; i++) {
    const s = lo + ((hi - lo) * i) / (N - 1);
    let pay = 0;
    for (const leg of legs) {
      const intrinsic = leg.kind === "call"
        ? Math.max(s - leg.strike, 0)
        : Math.max(leg.strike - s, 0);
      pay += (leg.action === "buy" ? 1 : -1) * leg.quantity * (intrinsic - leg.price);
    }
    xs.push(s);
    ys.push(pay * 100);
  }

  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const pad = Math.max(1, (yMax - yMin) * 0.12);
  const y0 = yMin - pad;
  const y1 = yMax + pad;

  const px = (s: number) => ((s - lo) / (hi - lo)) * width;
  const py = (v: number) => height - ((v - y0) / (y1 - y0)) * height;

  const path = xs.map((s, i) => `${i === 0 ? "M" : "L"}${px(s).toFixed(1)},${py(ys[i]!).toFixed(1)}`).join(" ");
  const zeroY = py(0);

  // Split the fill at zero so profit and loss read at a glance.
  const areaUp = `M${px(lo)},${zeroY} ` +
    xs.map((s, i) => `L${px(s).toFixed(1)},${py(Math.max(0, ys[i]!)).toFixed(1)}`).join(" ") +
    ` L${px(hi)},${zeroY} Z`;
  const areaDown = `M${px(lo)},${zeroY} ` +
    xs.map((s, i) => `L${px(s).toFixed(1)},${py(Math.min(0, ys[i]!)).toFixed(1)}`).join(" ") +
    ` L${px(hi)},${zeroY} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="none"
      style={{
        display: "block", background: "var(--bg-sunken)",
        border: "1px solid var(--border)", borderRadius: 6,
      }}
    >
      {expectedMove && expectedMove > 0 && (
        <rect
          x={px(spot - expectedMove)}
          width={Math.max(1, px(spot + expectedMove) - px(spot - expectedMove))}
          y={0}
          height={height}
          fill="var(--accent)"
          opacity={0.07}
        />
      )}
      <path d={areaUp} fill="var(--tone-good)" opacity={0.18} />
      <path d={areaDown} fill="var(--down)" opacity={0.15} />
      <line x1={0} x2={width} y1={zeroY} y2={zeroY} stroke="var(--border-strong)" />
      <line
        x1={px(spot)} x2={px(spot)} y1={0} y2={height}
        stroke="var(--text-faint)" strokeDasharray="3 3"
      />
      {(breakevens ?? []).map((b) => (
        <line
          key={b}
          x1={px(b)} x2={px(b)} y1={0} y2={height}
          stroke="var(--tone-warn)" strokeDasharray="2 2" opacity={0.8}
        />
      ))}
      {legs.map((l, i) => (
        <line
          key={`${l.kind}${l.strike}${i}`}
          x1={px(l.strike)} x2={px(l.strike)} y1={height - 6} y2={height}
          stroke="var(--accent)" strokeWidth={1.5}
        />
      ))}
      <path d={path} fill="none" stroke="var(--accent)" strokeWidth={1.8} />
    </svg>
  );
}
